/**
 * Portal Manta · KbSimul
 * Extraído de App.jsx v29.3 (via AST)
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { V } from '../../globals.js';
import { M } from '../../tokens/colors.js';
import { THl, TH } from '../../app-globals.js';

function KbSimul(){
  const [ext,setExt]=useState(50);
  const [tipo,setTipo]=useState('duplicacao'); // duplicacao | restauracao | implantacao
  const [relevo,setRelevo]=useState('ondulado'); // plano | ondulado | montanhoso
  const [pista,setPista]=useState('dupla'); // simples | dupla
  const [pavimento,setPavimento]=useState('cbuq'); // cbuq | tsd | concreto
  const [contexto,setContexto]=useState('rural'); // rural | urbano

  // Coeficientes paramétricos (baseados em Lotes PR + DNIT Vol.10)
  const COEF={
    // fatores multiplicativos por relevo (base: ondulado=1.0)
    relevoFat:{plano:0.82,ondulado:1.0,montanhoso:1.38},
    // CU base por tipologia em R$ M/km para pista dupla, pavimento CBUQ, relevo ondulado, rural
    cuBase:{duplicacao:5.58,restauracao:2.15,implantacao:8.40},
    // fator pista (dupla=1.0, simples=0.62)
    pistaFat:{dupla:1.0,simples:0.62},
    // fator pavimento (cbuq=1.0, tsd=0.78, concreto=1.45)
    pavFat:{cbuq:1.0,tsd:0.78,concreto:1.45},
    // fator contexto (rural=1.0, urbano=1.28 — desapropriação, desvio de tráfego, interferências)
    contextoFat:{rural:1.0,urbano:1.28},
    // volumes por km (m³/km) — varia com relevo
    terrVol:{plano:{corte:6500,aterro:5800},ondulado:{corte:18200,aterro:16400},montanhoso:{corte:42000,aterro:31000}},
    // limpeza/desmatamento (m²/km) — contexto
    limpeza:{rural:32000,urbano:11000},
    // drenagem (und de bueiros/km e m de galeria/km)
    drenFat:{plano:{bueirosKm:0.8,galeriaKm:45},ondulado:{bueirosKm:1.4,galeriaKm:85},montanhoso:{bueirosKm:2.1,galeriaKm:140}},
  };

  // Cálculo
  const cuAjustado=COEF.cuBase[tipo]*COEF.relevoFat[relevo]*COEF.pistaFat[pista]*COEF.pavFat[pavimento]*COEF.contextoFat[contexto];
  const capex=cuAjustado*ext;
  const faixa={min:capex*0.88,mid:capex,max:capex*1.15};

  // Estrutura de pavimento típica (cm)
  const PAV_ESTRUTURA={
    cbuq:[
      {camada:'Revestimento CBUQ',espessura:'5 cm',material:'Concreto Betuminoso Usinado a Quente'},
      {camada:'Binder',espessura:'4 cm',material:'CBUQ binder'},
      {camada:'Base granular',espessura:'20 cm',material:'BGS (Brita Graduada Simples)'},
      {camada:'Sub-base',espessura:'15 cm',material:'Solo estabilizado granulometricamente'},
      {camada:'Reforço de subleito',espessura:'20 cm',material:'Solo selecionado (CBR ≥ 20%)'},
    ],
    tsd:[
      {camada:'Revestimento TSD',espessura:'2,5 cm',material:'Tratamento Superficial Duplo'},
      {camada:'Imprimação',espessura:'—',material:'CM-30 · 1,2 l/m²'},
      {camada:'Base',espessura:'15 cm',material:'Macadame hidráulico ou solo-cimento'},
      {camada:'Sub-base',espessura:'15 cm',material:'Solo estabilizado'},
    ],
    concreto:[
      {camada:'Placa de concreto',espessura:'24 cm',material:'Concreto fck 35 MPa · placas 5×5m'},
      {camada:'Sub-base cimentada',espessura:'10 cm',material:'BGTC (Brita Graduada Tratada com Cimento)'},
      {camada:'Reforço de subleito',espessura:'20 cm',material:'Solo selecionado'},
    ],
  };

  // Volumes
  const terr=COEF.terrVol[relevo];
  const volCorte=terr.corte*ext;
  const volAterro=terr.aterro*ext;
  const emprestimoBalanco=Math.max(0,volAterro-volCorte*0.75); // 75% aproveitamento
  const bota_fora=Math.max(0,volCorte*0.75-volAterro);
  const limpezaM2=COEF.limpeza[contexto]*ext;
  const dren=COEF.drenFat[relevo];
  const bueiros=Math.round(dren.bueirosKm*ext);
  const galeriaM=Math.round(dren.galeriaKm*ext);

  const Input=({label,value,onChange,options,helper})=>(<div style={{flex:1,minWidth:140}}>
    <div style={{fontSize:9.5,fontWeight:700,color:V.g5,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>{label}</div>
    <select value={value} onChange={e=>onChange(e.target.value)} style={{width:'100%',padding:'7px 9px',border:`1px solid ${V.g2}`,borderRadius:5,fontSize:10.5,fontFamily:'inherit',background:'#fff',color:V.g7,outline:'none',cursor:'pointer'}}>
      {options.map(o=>(<option key={o.v} value={o.v}>{o.l}</option>))}
    </select>
    {helper&&<div style={{fontSize:8.5,color:V.g4,marginTop:3}}>{helper}</div>}
  </div>);

  const fmtNum=(v,dec=0)=>v.toLocaleString('pt-BR',{minimumFractionDigits:dec,maximumFractionDigits:dec});
  const fmtRS=v=>v>=1e3?'R$ '+(v/1e3).toFixed(2).replace('.',',')+' B':'R$ '+v.toFixed(1).replace('.',',')+' M';

  return(<div style={{padding:'12px 16px'}}>
    <div style={{marginBottom:10}}>
      <div style={{fontSize:12.5,fontWeight:700,color:V.br2}}>Simulações Paramétricas — Calculadora de Obra Rodoviária</div>
      <div style={{fontSize:10.5,color:V.g5,marginTop:2}}>Estimativa de quantidades e CAPEX a partir de parâmetros geométricos e de infraestrutura · coeficientes baseados em Lotes PR + DNIT Vol.10 + SICRO 2024</div>
    </div>

    {/* Formulário */}
    <div style={{background:'#fff',border:`1px solid ${V.g2}`,borderRadius:10,padding:'14px 16px',marginBottom:14}}>
      <div style={{fontSize:10.5,fontWeight:700,color:V.br,marginBottom:10}}>Parâmetros de entrada</div>
      {/* Extensão como input numérico */}
      <div style={{marginBottom:12}}>
        <div style={{fontSize:9.5,fontWeight:700,color:V.g5,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Extensão (km)</div>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <input type="range" min="1" max="300" value={ext} onChange={e=>setExt(Number(e.target.value))} style={{flex:1,accentColor:V.or}}/>
          <input type="number" min="1" max="1000" value={ext} onChange={e=>setExt(Math.max(1,Number(e.target.value)||1))} style={{width:80,padding:'6px 9px',border:`1px solid ${V.g2}`,borderRadius:5,fontSize:11,fontFamily:'monospace',fontWeight:700,color:V.or,textAlign:'right'}}/>
          <span style={{fontSize:11,color:V.g5,fontWeight:600}}>km</span>
        </div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:10}}>
        <Input label="Tipo de obra" value={tipo} onChange={setTipo} options={[
          {v:'duplicacao',l:'Duplicação'},
          {v:'restauracao',l:'Restauração'},
          {v:'implantacao',l:'Implantação nova'},
        ]} helper="Define CU base"/>
        <Input label="Pista" value={pista} onChange={setPista} options={[
          {v:'dupla',l:'Pista dupla'},
          {v:'simples',l:'Pista simples'},
        ]} helper="Nº de faixas por sentido"/>
        <Input label="Relevo" value={relevo} onChange={setRelevo} options={[
          {v:'plano',l:'Plano'},
          {v:'ondulado',l:'Ondulado'},
          {v:'montanhoso',l:'Montanhoso'},
        ]} helper="Afeta terraplenagem e CU"/>
        <Input label="Pavimento" value={pavimento} onChange={setPavimento} options={[
          {v:'cbuq',l:'CBUQ (flexível)'},
          {v:'tsd',l:'TSD (econômico)'},
          {v:'concreto',l:'Concreto (rígido)'},
        ]} helper="Tipo de revestimento"/>
        <Input label="Contexto" value={contexto} onChange={setContexto} options={[
          {v:'rural',l:'Rural'},
          {v:'urbano',l:'Urbano'},
        ]} helper="Interferências e desapropriação"/>
      </div>
    </div>

    {/* Card CAPEX estimado */}
    <div style={{background:V.or,color:'#fff',borderRadius:10,padding:'16px 20px',marginBottom:14}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:10}}>
        <div>
          <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',opacity:.9}}>CAPEX Total Estimado · {ext} km</div>
          <div style={{fontSize:24,fontWeight:700,fontFamily:'monospace',marginTop:2,letterSpacing:'-0.5px'}}>{fmtRS(faixa.mid)}</div>
          <div style={{fontSize:10,opacity:.85,marginTop:3}}>Faixa: {fmtRS(faixa.min)} — {fmtRS(faixa.max)} · variação ±12% (confiança paramétrica)</div>
        </div>
        <div style={{textAlign:'right'}}>
          <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em',opacity:.9}}>CU unitário</div>
          <div style={{fontSize:18,fontWeight:700,fontFamily:'monospace',marginTop:2}}>R$ {cuAjustado.toFixed(2).replace('.',',')} M/km</div>
          <div style={{fontSize:9.5,opacity:.85,marginTop:3}}>Multiplicadores: relevo × pista × pavim. × contexto</div>
        </div>
      </div>
    </div>

    {/* Resultados por disciplina */}
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(320px,1fr))',gap:10}}>
      {/* Terraplenagem */}
      <div style={{background:'#fff',border:`1px solid ${V.g2}`,borderRadius:10,padding:'12px 14px'}}>
        <div style={{fontSize:11,fontWeight:700,color:V.br,marginBottom:8,display:'flex',alignItems:'center',gap:6}}>
          <span style={{width:6,height:6,borderRadius:'50%',background:'#92400E',display:'inline-block'}}></span>
          Terraplenagem
        </div>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <tbody>
            <tr style={{borderBottom:`1px solid ${V.g1}`}}>
              <td style={{padding:'5px 0',fontSize:10.5,color:V.g7}}>Corte</td>
              <td style={{padding:'5px 0',fontSize:10.5,fontFamily:'monospace',textAlign:'right',fontWeight:700,color:V.br}}>{fmtNum(volCorte)} m³</td>
            </tr>
            <tr style={{borderBottom:`1px solid ${V.g1}`}}>
              <td style={{padding:'5px 0',fontSize:10.5,color:V.g7}}>Aterro</td>
              <td style={{padding:'5px 0',fontSize:10.5,fontFamily:'monospace',textAlign:'right',fontWeight:700,color:V.br}}>{fmtNum(volAterro)} m³</td>
            </tr>
            <tr style={{borderBottom:`1px solid ${V.g1}`}}>
              <td style={{padding:'5px 0',fontSize:10.5,color:V.g7}}>Empréstimo (balanço)</td>
              <td style={{padding:'5px 0',fontSize:10.5,fontFamily:'monospace',textAlign:'right',fontWeight:600,color:emprestimoBalanco>0?V.or:V.g4}}>{fmtNum(emprestimoBalanco)} m³</td>
            </tr>
            <tr>
              <td style={{padding:'5px 0',fontSize:10.5,color:V.g7}}>Bota-fora</td>
              <td style={{padding:'5px 0',fontSize:10.5,fontFamily:'monospace',textAlign:'right',fontWeight:600,color:bota_fora>0?V.or:V.g4}}>{fmtNum(bota_fora)} m³</td>
            </tr>
          </tbody>
        </table>
        <div style={{fontSize:9,color:V.g4,marginTop:6,fontStyle:'italic'}}>Ref. DNIT ISC-022 · Aproveitamento típico 75%</div>
      </div>

      {/* Limpeza e desmatamento */}
      <div style={{background:'#fff',border:`1px solid ${V.g2}`,borderRadius:10,padding:'12px 14px'}}>
        <div style={{fontSize:11,fontWeight:700,color:V.br,marginBottom:8,display:'flex',alignItems:'center',gap:6}}>
          <span style={{width:6,height:6,borderRadius:'50%',background:V.gn,display:'inline-block'}}></span>
          Limpeza e Desmatamento
        </div>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <tbody>
            <tr style={{borderBottom:`1px solid ${V.g1}`}}>
              <td style={{padding:'5px 0',fontSize:10.5,color:V.g7}}>Limpeza geral</td>
              <td style={{padding:'5px 0',fontSize:10.5,fontFamily:'monospace',textAlign:'right',fontWeight:700,color:V.br}}>{fmtNum(limpezaM2)} m²</td>
            </tr>
            <tr style={{borderBottom:`1px solid ${V.g1}`}}>
              <td style={{padding:'5px 0',fontSize:10.5,color:V.g7}}>Desmatamento</td>
              <td style={{padding:'5px 0',fontSize:10.5,fontFamily:'monospace',textAlign:'right',fontWeight:600,color:V.g7}}>{fmtNum(limpezaM2*0.65)} m²</td>
            </tr>
            <tr>
              <td style={{padding:'5px 0',fontSize:10.5,color:V.g7}}>Destocamento</td>
              <td style={{padding:'5px 0',fontSize:10.5,fontFamily:'monospace',textAlign:'right',fontWeight:600,color:V.g7}}>{fmtNum(limpezaM2*0.45)} m²</td>
            </tr>
          </tbody>
        </table>
        <div style={{fontSize:9,color:V.g4,marginTop:6,fontStyle:'italic'}}>Faixa domínio {contexto==='rural'?'40m rural':'20m urbano'} · Ref. NBR 13133</div>
      </div>

      {/* Drenagem */}
      <div style={{background:'#fff',border:`1px solid ${V.g2}`,borderRadius:10,padding:'12px 14px'}}>
        <div style={{fontSize:11,fontWeight:700,color:V.br,marginBottom:8,display:'flex',alignItems:'center',gap:6}}>
          <span style={{width:6,height:6,borderRadius:'50%',background:V.bl,display:'inline-block'}}></span>
          Drenagem
        </div>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <tbody>
            <tr style={{borderBottom:`1px solid ${V.g1}`}}>
              <td style={{padding:'5px 0',fontSize:10.5,color:V.g7}}>Bueiros tubulares</td>
              <td style={{padding:'5px 0',fontSize:10.5,fontFamily:'monospace',textAlign:'right',fontWeight:700,color:V.br}}>{bueiros} und</td>
            </tr>
            <tr style={{borderBottom:`1px solid ${V.g1}`}}>
              <td style={{padding:'5px 0',fontSize:10.5,color:V.g7}}>Galerias celulares</td>
              <td style={{padding:'5px 0',fontSize:10.5,fontFamily:'monospace',textAlign:'right',fontWeight:700,color:V.br}}>{galeriaM} m</td>
            </tr>
            <tr>
              <td style={{padding:'5px 0',fontSize:10.5,color:V.g7}}>Sarjetas (ambos lados)</td>
              <td style={{padding:'5px 0',fontSize:10.5,fontFamily:'monospace',textAlign:'right',fontWeight:600,color:V.g7}}>{fmtNum(ext*2000)} m</td>
            </tr>
          </tbody>
        </table>
        <div style={{fontSize:9,color:V.g4,marginTop:6,fontStyle:'italic'}}>Ref. IPR-736 · período de retorno 25 anos</div>
      </div>

      {/* Estrutura do pavimento */}
      <div style={{background:'#fff',border:`1px solid ${V.g2}`,borderRadius:10,padding:'12px 14px'}}>
        <div style={{fontSize:11,fontWeight:700,color:V.br,marginBottom:8,display:'flex',alignItems:'center',gap:6}}>
          <span style={{width:6,height:6,borderRadius:'50%',background:V.or,display:'inline-block'}}></span>
          Estrutura do Pavimento · {pavimento.toUpperCase()}
        </div>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead><tr style={{background:V.g1}}>
            <th style={{...THl,padding:'4px 8px',fontSize:9,color:V.g5,width:'45%'}}>Camada</th>
            <th style={{...TH,padding:'4px 8px',fontSize:9,color:V.g5,width:'20%'}}>Esp.</th>
            <th style={{...THl,padding:'4px 8px',fontSize:9,color:V.g5}}>Material</th>
          </tr></thead>
          <tbody>{PAV_ESTRUTURA[pavimento].map((p,i)=>(<tr key={i} style={{borderBottom:`1px solid ${V.g1}`}}>
            <td style={{padding:'4px 8px',fontSize:10.5,color:V.g7,fontWeight:600}}>{p.camada}</td>
            <td style={{padding:'4px 8px',fontSize:10,fontFamily:'monospace',textAlign:'right',color:V.or,fontWeight:700}}>{p.espessura}</td>
            <td style={{padding:'4px 8px',fontSize:9.5,color:V.g5}}>{p.material}</td>
          </tr>))}</tbody>
        </table>
        <div style={{fontSize:9,color:V.g4,marginTop:6,fontStyle:'italic'}}>Ref. DNIT ISC-031 · dimensionamento AASHTO 93</div>
      </div>
    </div>

    <div style={{marginTop:14,display:'flex',gap:8,justifyContent:'flex-end'}}>
      <button style={{padding:'8px 14px',background:'#fff',border:`1px solid ${V.g2}`,borderRadius:6,fontSize:10,color:V.g5,cursor:'pointer',fontFamily:'inherit'}}>Exportar como XLSX</button>
      <button style={{padding:'8px 14px',background:V.or,color:'#fff',border:'none',borderRadius:6,fontSize:10,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Salvar simulação</button>
    </div>

    <div style={{marginTop:12,padding:'10px 12px',background:V.g1,borderRadius:8,fontSize:10,color:V.g5,lineHeight:1.5}}>
      <b style={{color:V.br}}>Base de cálculo:</b> CU base (R$ 5,58 M/km duplicação) calibrado a partir do Lote 3 PR (rateio pró-rata). Fatores multiplicativos derivados de DNIT Vol.10 (Tab. 4.3) e SICRO 2024. Volumes de terraplenagem usam coeficientes típicos por relevo da ISC-022. Drenagem segue IPR-736. Faixa ±12% reflete incerteza paramétrica típica — para orçamento executivo, use o Extrator ANTT + refinamento via Dados Manta.
    </div>
  </div>);
}

export { KbSimul };
export default KbSimul;
